/**
 * Monthly spend bars. Scroll-driven reveal via animation-timeline: view().
 * No chart lib. Linear scaling against month max.
 */
import { store } from '../store.ts';
import { formatINR } from '../types.ts';

export class HisaabMonthlyBars extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.totals.by_month, s.summary.totals.monthly_avg);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byMonth: Record<string, number>, avg: number): void {
    const months = Object.entries(byMonth);
    if (months.length === 0) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const max = Math.max(...months.map(([, v]) => v), 1);

    const bars = months
      .map(([month, value]) => {
        const pct = (value / max) * 100;
        const label = monthLabel(month);
        const above = value > avg;
        return /* html */ `
<div class="row" style="--pct:${pct}%">
  <div class="month">${label}</div>
  <div class="bar-wrap">
    <div class="bar ${above ? 'above' : ''}"></div>
    <div class="bar-rule"></div>
  </div>
  <div class="value">${formatINR(value)}</div>
</div>`;
      })
      .join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.card {
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 26px 22px 30px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
}
.card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(ellipse 90% 50% at 50% 0%, oklch(20% 0.05 280 / 0.5), transparent 60%);
  z-index: -1;
}
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 6px;
}
.subtitle {
  font-family: var(--display);
  font-style: italic;
  color: var(--ink-dim);
  font-size: 14px;
  margin-bottom: 22px;
  letter-spacing: -0.005em;
}
.subtitle b {
  font-style: normal;
  color: var(--saffron);
  font-weight: 600;
  font-feature-settings: 'tnum' 1, 'lnum' 1;
}
.rows { display: grid; gap: 14px; }
.row {
  display: grid;
  grid-template-columns: 64px 1fr 100px;
  gap: 14px;
  align-items: center;
}
.month {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-dim);
}
.bar-wrap {
  position: relative;
  height: 12px;
  background: oklch(20% 0.04 280);
  border-radius: 999px;
  overflow: hidden;
}
.bar {
  position: relative;
  height: 100%;
  width: var(--pct);
  background: linear-gradient(90deg, var(--saffron-dim), var(--saffron));
  border-radius: 999px;
  transform-origin: left;
  animation: grow 720ms cubic-bezier(0.32, 0.72, 0, 1) both;
}
.bar.above {
  background: linear-gradient(90deg, var(--rose), oklch(72% 0.2 25));
  box-shadow: 0 0 18px oklch(72% 0.2 25 / 0.35);
}
/* hairline rule overlay between bars */
.bar-rule {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(90deg,
    transparent 0%,
    oklch(98% 0.02 80 / 0.06) 50%,
    transparent 100%);
}
.value {
  font-family: var(--display);
  font-size: 18px;
  text-align: right;
  color: var(--ink);
  font-weight: 600;
  font-feature-settings: 'tnum' 1, 'lnum' 1, 'ss01' 1;
  letter-spacing: -0.005em;
}

/* scroll-driven row-by-row reveal */
@supports (animation-timeline: view()) {
  .row {
    animation: row-in linear both;
    animation-timeline: view();
    animation-range: entry 0% cover 25%;
  }
}
@keyframes row-in {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes grow {
  from { transform: scaleX(0); opacity: 0; }
  to   { transform: scaleX(1); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .row, .bar { animation: none; }
}
</style>
<div class="card">
  <div class="title">Monthly chot</div>
  <div class="subtitle">Avg <b>${formatINR(avg)}</b> · red bars cross the line</div>
  <div class="rows">${bars}</div>
</div>
`;
  }
}

function monthLabel(yyyymm: string): string {
  const [, m] = yyyymm.split('-');
  const idx = (Number(m) || 1) - 1;
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][idx] ?? yyyymm;
}

customElements.define('hisaab-monthly-bars', HisaabMonthlyBars);
